/* eslint-disable no-param-reassign */
import type { Draft, PayloadAction } from "@reduxjs/toolkit";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { castDraft } from "immer";
import { uniqueId } from "lodash-es";
import { ensureExhaustive } from "@dhau/lang-extras";
import { transportPictureConfigurationToCore } from "@brickme/project-core/src/model/transport.ts";
import removeTypenames from "@brickme/project-core/src/utils/remove-typenames.ts";
import type {
	GetBrickArtDesignQuery,
	GetBrickArtDesignQueryVariables,
} from "~/api/get-brick-art-design-query.generated.ts";
import getBrickArtDesignQuery from "~/api/get-brick-art-design-query.graphql";
import type { StoreThunkApiConfig } from "~/store-config.ts";
import {
	selectImageByUrl,
	startNewProject,
	selectImageByPicToBrick,
} from "~/features/workspace/store-slice.ts";
import startProject from "~/features/workspace/start-project.ts";
import type { Command } from "~/features/commands/command.ts";
import type { State as ReferenceState } from "~/features/reference/state.ts";
import type { State as UserState } from "~/features/user/store-slice.ts";
import { loadSavedProjectById } from "~/features/user/store-slice.ts";
import { loadGiftRedemption } from "~/features/gift/store-slice.ts";
import type { State as GiftState } from "~/features/gift/store-slice.ts";
import selectDefaultHangingType from "~/features/order/select-default-hanging-type.ts";
import { ensureLoadedOnceOffLoad } from "~/utils/loading.ts";
import type { EditorMenu } from "./editor-menu.ts";
import type { InitialAction } from "./initial-action.ts";

type InitialActionStatus = "pending" | "performing" | "done";

type LoginPromptDetails = {
	readonly title: string;
};

type State = {
	readonly hasDismissedMobileWarning: boolean;
	readonly selectedEditorMenu: EditorMenu;
	readonly showPreview: boolean;
	readonly showCreateBrickArt: boolean;
	readonly isTutorialOpen: boolean;
	readonly isCompleteOpen: boolean;
	readonly isSimpleCreatorOpen: boolean;
	readonly isSavedDesignsOpen: boolean;

	readonly changeSizeErasesPenMessageStatus?: Command | "seen";
	readonly premiumColoursMessageStatus?: "show" | "seen";
	readonly firstTimeDrawToolStatus?: "show" | "seen";
	readonly initialAction?: InitialAction;
	readonly initialActionStatus: InitialActionStatus;
	readonly ref?: string;
	readonly guestPromoOpen: boolean;
	readonly showLoginPrompt: LoginPromptDetails | false;
	readonly isCompleteLoginPromptOpen: boolean;
	readonly basePlateConversionWarningOpen: boolean;
	readonly getStartedComplete: boolean;
};

const initialState: State = {
	hasDismissedMobileWarning: false,
	selectedEditorMenu: "size",
	showPreview: false,
	showCreateBrickArt: false,
	isTutorialOpen: false,
	isSavedDesignsOpen: false,
	isCompleteOpen: false,

	isSimpleCreatorOpen: false,
	changeSizeErasesPenMessageStatus: undefined,
	premiumColoursMessageStatus: undefined,
	firstTimeDrawToolStatus: undefined,
	initialActionStatus: "pending",
	initialAction: undefined,
	ref: undefined,
	guestPromoOpen: false,
	showLoginPrompt: false,
	isCompleteLoginPromptOpen: false,
	basePlateConversionWarningOpen: false,
	getStartedComplete: false,
};

const name = "nav";

const loadBrickArtDesign = createAsyncThunk<
	void,
	string,
	StoreThunkApiConfig<{ reference: ReferenceState; user: UserState }>
>(
	`${name}/loadBrickArtDesign`,
	async (designId, { dispatch, getState, extra: { apiClient } }) => {
		const data = await apiClient.request<
			GetBrickArtDesignQuery,
			GetBrickArtDesignQueryVariables
		>(getBrickArtDesignQuery, { id: designId });

		const {
			reference: { systemPalette },
			user,
		} = getState();
		const systemPaletteData = ensureLoadedOnceOffLoad(
			systemPalette,
			"System palette not loaded",
		);
		const design = data.brickArtDesign;
		// TODO: Something with warnings
		const [currentPicture, ...otherPictures] = design.variantPictures
			.map(removeTypenames)
			.map((picture) => {
				const { result, warnings } = transportPictureConfigurationToCore(
					// TODO: Work towards better transport transform functions with api schemas
					picture as any,
					systemPaletteData,
				);
				if (warnings.size > 0) {
					console.warn("Got parse warnings", Array.from(warnings));
				}
				return result;
			});

		const whoFor = user?.whoFor;
		const giftType = user?.giftType;
		await dispatch(
			startProject({
				project: {
					faceBoundingBoxes: design.faceBoundingBoxes ?? undefined,
					currentPicture,
					otherVersions: otherPictures.map((picture) => ({
						id: uniqueId(),
						picture,
					})),
					updatedAt: Date.now(),
					whoFor,
					giftType,
				},
				// TODO: Would probably want to optimise buffer to smallest size (when
				// pass in a url like this no optimisation done)
				sourceImageBitmapOrUrl: design.sourceImageUrl,
				backgroundMaskImageUrl: design.backgroundMaskImageUrl ?? undefined,
				enhanceFacesImageUrl: design.enhanceFacesImageUrl ?? undefined,
				posteriseImage: (() => {
					const { posteriseImage } = design;
					if (
						posteriseImage?.type === "success" &&
						posteriseImage.url !== null
					) {
						return {
							type: "success",
							url: posteriseImage.url,
						};
					}
					if (posteriseImage?.type === "unavailable") {
						return {
							type: "unavailable",
							message: posteriseImage.message ?? "General error",
						};
					}
					return undefined;
				})(),
				colorisationImageUrl: design.colorisationImageUrl ?? undefined,
			}),
		);
	},
);

const performInitialAction = createAsyncThunk<
	void,
	undefined,
	StoreThunkApiConfig<{
		nav: State;
		reference: ReferenceState;
		user: UserState;
		gift: GiftState;
	}>
>(`${name}/performInitialAction`, async (_, { getState, dispatch }) => {
	const {
		nav: { initialAction },
	} = getState();
	if (!initialAction) {
		return;
	}

	switch (initialAction.type) {
		case "loadSourceImage":
			await dispatch(selectImageByUrl(initialAction.imageUrl));
			await dispatch(selectDefaultHangingType());
			break;
		case "startPicToBrick":
			await dispatch(selectImageByPicToBrick(initialAction.picId));
			await dispatch(selectDefaultHangingType());
			break;
		case "loadProject":
			await dispatch(loadSavedProjectById(initialAction.projectId));
			break;
		case "loadBrickArtDesign":
			await dispatch(loadBrickArtDesign(initialAction.designId));
			break;
		case "giftRedemption":
			await dispatch(
				loadGiftRedemption({
					sessionId: initialAction.sessionId,
					variantId: initialAction.variantId,
					framingVariantId: initialAction.framingVariantId,
					framingVariantQty: initialAction.framingVariantQty,
				}),
			);
			break;
		default:
			ensureExhaustive(initialAction);
	}
});

const slice = createSlice({
	name,
	initialState,
	reducers: {
		selectEditorMenu: (state, action: PayloadAction<EditorMenu>) => {
			state.selectedEditorMenu = action.payload;
		},
		acknowledgeMobileEditorMessage: (state) => {
			state.hasDismissedMobileWarning = true;
		},
		showPremiumColoursMessage: (state) => {
			state.premiumColoursMessageStatus = "show";
		},
		acknowledgePremiumColoursMessage: (state) => {
			state.premiumColoursMessageStatus = "seen";
		},
		showFirstTimeDrawToolStatus: (state) => {
			state.firstTimeDrawToolStatus = "show";
		},
		acknowledgeFirstTimeDrawToolStatus: (state) => {
			state.firstTimeDrawToolStatus = "seen";
		},
		showChangeSizeErasesPenMessage: (state, action: PayloadAction<Command>) => {
			state.changeSizeErasesPenMessageStatus = castDraft(action.payload);
		},
		acknowledgeChangeSizeErasesPenMessage: (state) => {
			state.changeSizeErasesPenMessageStatus = "seen";
		},
		showPreview: (state) => {
			state.showPreview = true;
		},
		hidePreview: (state) => {
			state.showPreview = false;
		},
		showCreateBrickArt: (state) => {
			state.showCreateBrickArt = true;
		},
		hideCreateBrickArt: (state) => {
			state.showCreateBrickArt = false;
		},
		openTutorial: (state) => {
			state.isTutorialOpen = true;
		},
		closeTutorial: (state) => {
			state.isTutorialOpen = false;
		},
		openSavedDesigns: (state) => {
			state.isSavedDesignsOpen = true;
		},
		closeSavedDesigns: (state) => {
			state.isSavedDesignsOpen = false;
		},
		openComplete: (state) => {
			state.isCompleteOpen = true;
		},
		showCompleteLoginPrompt: (state) => {
			state.isCompleteLoginPromptOpen = true;
		},
		hideCompleteLoginPrompt: (state) => {
			state.isCompleteLoginPromptOpen = false;
		},
		closeComplete: (state) => {
			state.isCompleteOpen = false;
		},
		closeBasePlatConversionWarning: (state) => {
			state.basePlateConversionWarningOpen = false;
		},

		hideGuestPromo: (state) => {
			state.guestPromoOpen = false;
		},
		showLoginPrompt: (state, action: PayloadAction<LoginPromptDetails>) => {
			state.showLoginPrompt = action.payload;
		},
		hideLoginPrompt: (state) => {
			state.showLoginPrompt = false;
		},
		switchToAdvancedCreator: (state) => {
			state.isSimpleCreatorOpen = false;
		},
		switchToSimpleCreator: (state) => {
			state.isSimpleCreatorOpen = true;
		},
		setGetStartedComplete(state, action: PayloadAction<boolean>) {
			state.getStartedComplete = action.payload;
		},
	},
	extraReducers(builder) {
		builder.addCase(
			performInitialAction.pending.type,
			(state: Draft<State>) => {
				state.initialActionStatus = "performing";
			},
		);
		builder.addCase(
			performInitialAction.rejected.type,
			(state: Draft<State>) => {
				state.initialActionStatus = "done";
			},
		);
		builder.addCase(
			performInitialAction.fulfilled.type,
			(state: Draft<State>) => {
				state.initialActionStatus = "done";
			},
		);
		builder.addCase(startNewProject.type, (state: Draft<State>) => {
			state.showPreview = false;
			state.selectedEditorMenu = initialState.selectedEditorMenu;
		});
		// [loadSavedProject.fulfilled.type]: (
		//   state: Draft<State>,
		//   action: Draft<ReturnType<typeof loadSavedProject["fulfilled"]>>
		// ) => {
		// Disabled atm, but keep here in case need again in future
		// state.basePlateConversionWarningOpen =
		//   action.payload.transformedToSmallBaseplates;
		// },
	},
});

export const {
	acknowledgeMobileEditorMessage,
	selectEditorMenu,
	showPreview,
	hidePreview,
	showPremiumColoursMessage,
	acknowledgePremiumColoursMessage,
	showChangeSizeErasesPenMessage,
	acknowledgeChangeSizeErasesPenMessage,
	showFirstTimeDrawToolStatus,
	acknowledgeFirstTimeDrawToolStatus,
	openTutorial,
	closeTutorial,
	closeBasePlatConversionWarning,
	closeComplete,
	openComplete,
	openSavedDesigns,
	closeSavedDesigns,

	hideGuestPromo,
	showLoginPrompt,
	hideLoginPrompt,
	showCompleteLoginPrompt,
	hideCompleteLoginPrompt,
	switchToAdvancedCreator,
	switchToSimpleCreator,
	showCreateBrickArt,
	hideCreateBrickArt,
	setGetStartedComplete,
} = slice.actions;
export { initialState, performInitialAction };
export type { InitialActionStatus, State };
export default slice.reducer;
